import * as React from "react";
import { CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Key, Shield, Loader2, MousePointerClick, Lock } from "lucide-react";
import { FaDigitalOcean, FaAws, FaMicrosoft } from "react-icons/fa6";
import { SiGooglecloud } from "react-icons/si";
import { ProjectContext } from "@/context/project";

interface SshKeyGeneratorProps {
  projectId: string;
  onSshKeyGenerated: (data: { sshPublicKey: string }) => void;
  project: {
    status?: string;
    sshPublicKey?: string;
  };
}

export function SshKeyGenerator({
  projectId,
  onSshKeyGenerated,
  project,
}: SshKeyGeneratorProps) {
  const { isDigitalOceanConnected, checkDigitalOceanConnection } = React.useContext(ProjectContext);
  const [isGeneratingKey, setIsGeneratingKey] = React.useState(false);
  const [generateSshMessage, setGenerateSshMessage] = React.useState("");

  // AWS State
  const [awsAccessKey, setAwsAccessKey] = React.useState("");
  const [awsSecretKey, setAwsSecretKey] = React.useState("");

  // GCP State
  const [gcpServiceAccount, setGcpServiceAccount] = React.useState("");

  // Azure State
  const [azureSubscriptionId, setAzureSubscriptionId] = React.useState("");
  const [azureTenantId, setAzureTenantId] = React.useState("");
  const [azureClientId, setAzureClientId] = React.useState("");
  const [azureClientSecret, setAzureClientSecret] = React.useState("");

  // Check if we just returned from OAuth callback
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const justConnected = urlParams.get('do_connected');
    
    if (justConnected === 'true') {
      checkDigitalOceanConnection();
      // Clean up URL parameter
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('do_connected');
      window.history.replaceState({}, '', newUrl);
    }
  }, [projectId, checkDigitalOceanConnection]);




  const handleGenerateSshKeys = async () => {
    setIsGeneratingKey(true);
    setGenerateSshMessage("Initiating SSH key generation...");
    try {
      const response = await fetch(
        `${
          import.meta.env.VITE_PUBLIC_API_ENDPOINT
        }/project/${projectId}/generate-keys`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
          credentials: "include",
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = "Failed to generate SSH keys.";
        try {
          const errorResult = JSON.parse(errorText);
          errorMessage = errorResult.error?.message || errorMessage;
        } catch (e) {
          console.log("Error parsing error response JSON:", e);
          errorMessage = errorText.slice(0, 100) || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      if (result.data?.publicKey) {
        setGenerateSshMessage("SSH key generation completed successfully!");
        onSshKeyGenerated({ sshPublicKey: result.data.publicKey });
      } else {
        setGenerateSshMessage(
          result.error?.message ||
            "SSH key generation failed. Please check logs.",
        );
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred.";
      setGenerateSshMessage(`Error: ${errorMessage}`);
    } finally {
      setIsGeneratingKey(false);
    }
  };

  const DeploymentInfo = () => (
    <div className="rounded-lg">
      <h4 className="mb-2 flex gap-1 text-sm font-medium">
        <MousePointerClick className="h-4 w-4" />
        <span>Clicking the button will:</span>
      </h4>
      <div className="mb-4 text-xs text-muted-foreground">
        <ul className="list-disc pl-5">
          <li>Generate secure SSH keys for server access</li>
          <li>Upload the public key to your cloud provider</li>
          <li>
            Securely store the key for this project for future
            deployments
          </li>
        </ul>
      </div>
      {generateSshMessage && (
        <p
          className={`mb-4 text-sm ${
            generateSshMessage.startsWith("Error:")
              ? "text-red-500"
              : "text-green-500"
          }`}
        >
          {generateSshMessage}
        </p>
      )}
    </div>
  );

  return (
    <div>
      <div className="ml-1 space-y-6">
        {project?.sshPublicKey ||
        (project?.status &&
          ["configured", "provisioned", "redeployed", "deleted"].includes(
            project?.status,
          )) ? (
          <div>
            <p className="text-sm font-medium text-muted-foreground">
              Public SSH Key successfully generated and added to your
              account.
            </p>
            {project?.sshPublicKey && (
              <pre className="mt-2 rounded-md bg-slate-100 p-2 text-xs">
                <code>{project.sshPublicKey}</code>
              </pre>
            )}
          </div>
        ) : (
          <div className="space-y-4">
             <Tabs defaultValue="digital-ocean" className="w-full">
              <TabsList className="grid w-full grid-cols-4 my-4 h-auto gap-1">
                <TabsTrigger value="digital-ocean" className="flex items-center gap-2 py-3">
                    <FaDigitalOcean className="h-4 w-4 text-[#0080FF]" />
                    Digital Ocean
                </TabsTrigger>
                
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="w-full">
                       <TabsTrigger value="aws" disabled className="w-full flex items-center gap-2 py-3 disabled:opacity-50 disabled:cursor-not-allowed">
                           <FaAws className="h-4 w-4 text-black" />
                          AWS
                      </TabsTrigger>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Coming Soon</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="w-full">
                       <TabsTrigger value="gcp" disabled className="w-full flex items-center gap-2 py-3 disabled:opacity-50 disabled:cursor-not-allowed">
                          <SiGooglecloud className="h-4 w-4 text-[#4285F4]" />
                          GCP
                      </TabsTrigger>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Coming Soon</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="w-full">
                       <TabsTrigger value="azure" disabled className="w-full flex items-center gap-2 py-3 disabled:opacity-50 disabled:cursor-not-allowed">
                          <FaMicrosoft className="h-4 w-4 text-[#0078D4]" />
                          Azure
                      </TabsTrigger>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Coming Soon</p>
                  </TooltipContent>
                </Tooltip>
              </TabsList>

              <TabsContent value="digital-ocean" className="space-y-4 px-6">
                <div className="mb-4 flex flex-col gap-4">
                  <>
                    {!isDigitalOceanConnected ? (
                      <>
                        {project?.status && ["generated"].includes(project?.status) && (
                          <CardHeader className="pl-0 mb-2">
                            <CardTitle className="flex items-center gap-2">
                              <Shield className="h-5 w-5" />
                              Security & SSH Access
                            </CardTitle>
                            <CardDescription>
                              Connect your DigitalOcean account to generate and upload an SSH key.
                            </CardDescription>
                          </CardHeader>
                        )}
                        <div>
                          <Button
                            onClick={() => {
                              window.location.href = `${import.meta.env.VITE_PUBLIC_API_ENDPOINT}/auth/digitalocean/authorize?projectId=${projectId}`;
                            }}
                            variant="outline"
                            className="w-full gap-2"
                          >
                            <FaDigitalOcean className="h-4 w-4 text-[#0080FF]" /> Connect
                            with DigitalOcean
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-green-700">
                            <FaDigitalOcean className="h-5 w-5 text-[#0080FF]" />
                            <span className="font-medium">Connected to DigitalOcean</span>
                          </div>
                        </div>
                        <p className="mt-1 text-sm text-green-600">
                          Your DigitalOcean account is connected. You can generate SSH keys below.
                        </p>
                      </div>
                    )}
                  </>
                </div>
                <DeploymentInfo />
                <Button
                    onClick={handleGenerateSshKeys}
                    disabled={isGeneratingKey || !isDigitalOceanConnected}
                    size="sm"
                    className="float-right"
                  >
                    {isGeneratingKey ? (
                      <>
                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Lock className="mr-1 h-4 w-4" />
                        Generate & Upload SSH Key
                      </>
                    )}
                  </Button>
              </TabsContent>

              <TabsContent value="aws" className="space-y-4 px-6">
                <div className="mb-4 flex flex-col gap-2">
                  <Label htmlFor="awsAccessKey" className="flex items-center gap-1">
                    <Key className="h-4 w-4" />
                    AWS Access Key ID
                  </Label>
                  <Input
                    id="awsAccessKey"
                    type="text"
                    placeholder="AKIA..."
                    value={awsAccessKey}
                    onChange={(e) => setAwsAccessKey(e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>
                <div className="mb-4 flex flex-col gap-2">
                  <Label htmlFor="awsSecretKey" className="flex items-center gap-1">
                    <Lock className="h-4 w-4" />
                    AWS Secret Access Key
                  </Label>
                  <Input
                    id="awsSecretKey"
                    type="password"
                    placeholder="Secret Key..."
                    value={awsSecretKey}
                    onChange={(e) => setAwsSecretKey(e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>
                <DeploymentInfo />
                <Button disabled size="sm" className="float-right">
                    Coming Soon
                </Button>
              </TabsContent>

              <TabsContent value="gcp" className="space-y-4 px-6">
                 <div className="mb-4 flex flex-col gap-2">
                  <Label htmlFor="gcpServiceAccount" className="flex items-center gap-1">
                    <Key className="h-4 w-4" />
                    Service Account JSON
                  </Label>
                  <Textarea
                    id="gcpServiceAccount"
                    placeholder="{ ... }"
                    value={gcpServiceAccount}
                    onChange={(e) => setGcpServiceAccount(e.target.value)}
                    className="font-mono text-sm min-h-[100px]"
                  />
                </div>
                <DeploymentInfo />
                <Button disabled size="sm" className="float-right">
                    Coming Soon
                </Button>
              </TabsContent>

              <TabsContent value="azure" className="space-y-4 px-6">
                <div className="grid grid-cols-2 gap-4">
                   <div className="flex flex-col gap-2">
                    <Label htmlFor="azureSubscriptionId">Subscription ID</Label>
                    <Input
                      id="azureSubscriptionId"
                      value={azureSubscriptionId}
                      onChange={(e) => setAzureSubscriptionId(e.target.value)}
                      className="font-mono text-sm"
                    />
                  </div>
                   <div className="flex flex-col gap-2">
                    <Label htmlFor="azureTenantId">Tenant ID</Label>
                    <Input
                      id="azureTenantId"
                      value={azureTenantId}
                      onChange={(e) => setAzureTenantId(e.target.value)}
                      className="font-mono text-sm"
                    />
                  </div>
                   <div className="flex flex-col gap-2">
                    <Label htmlFor="azureClientId">Client ID</Label>
                    <Input
                      id="azureClientId"
                      value={azureClientId}
                      onChange={(e) => setAzureClientId(e.target.value)}
                      className="font-mono text-sm"
                    />
                  </div>
                   <div className="flex flex-col gap-2">
                    <Label htmlFor="azureClientSecret">Client Secret</Label>
                    <Input
                      id="azureClientSecret"
                      type="password"
                      value={azureClientSecret}
                      onChange={(e) => setAzureClientSecret(e.target.value)}
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
                <DeploymentInfo />
                <Button disabled size="sm" className="float-right">
                    Coming Soon
                </Button>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>
    </div>
  );
}
